/**
 * Tenant Utilities
 * Helper functions for handling multi-tenant operations in the frontend
 */

// Environment variables for tenant configuration
const DEFAULT_TENANT_SLUG = (import.meta as any).env?.VITE_DEFAULT_TENANT_SLUG || 'demo';
const SINGLE_TENANT_MODE = (import.meta as any).env?.VITE_SINGLE_TENANT_MODE === 'true';

// Domains that always use default tenant (configurable via env)
const SINGLE_TENANT_DOMAINS = [
  'localhost',
  '127.0.0.1',
  'app.cotaskr.com',
  ...(((import.meta as any).env?.VITE_SINGLE_TENANT_DOMAINS || '').split(',').filter(Boolean))
];

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  domain?: string;
  plan?: string;
  usageTier?: 'starter' | 'growth' | 'enterprise';
  status: 'ACTIVE' | 'SUSPENDED' | 'TRIAL' | 'CANCELLED';
  maxUsers: number;
  maxProjects: number;
  settings?: {
    logoUrl?: string;
    brandColor?: string;
    [key: string]: any;
  };
}

/**
 * Extract tenant slug from hostname
 * Examples:
 *   - acme.co-taskr.com → 'acme'
 *   - techcorp.co-taskr.com → 'techcorp'
 *   - localhost:5173 → derived from stored tenant info (never hardcoded)
 *   - co-taskr.com → null (main site, no tenant)
 */
export function getTenantSlug(): string | null {
  const hostname = window.location.hostname;

  // If in single-tenant mode, always use default tenant
  if (SINGLE_TENANT_MODE) {
    return DEFAULT_TENANT_SLUG;
  }

  // Check if this is a single-tenant domain
  const isSingleTenantDomain = 
    SINGLE_TENANT_DOMAINS.some(domain => hostname === domain || hostname.startsWith(`${domain}:`)) ||
    hostname.includes('azurestaticapps.net');

  if (isSingleTenantDomain) {
    // 1. Try session-storage tenant set on every login / register
    try {
      const raw = sessionStorage.getItem('tenant');
      if (raw) {
        const t = JSON.parse(raw) as { slug?: string };
        if (t?.slug) return t.slug;
      }
    } catch { /* ignore */ }

    // 2. Fall back to the user object persisted in localStorage
    try {
      const prefix = (import.meta as any).env?.VITE_STORAGE_PREFIX ?? 'cotaskr_';
      const raw = localStorage.getItem(`${prefix}user`);
      if (raw) {
        const u = JSON.parse(raw) as { tenant?: { slug?: string } };
        if (u?.tenant?.slug) return u.tenant.slug;
      }
    } catch { /* ignore */ }

    // 3. No stored tenant yet — login resolves tenant from the user's email.
    return null;
  }

  // Check for subdomain (multi-tenant setup like acme.cotaskr.com)
  const parts = hostname.split('.');

  if (parts.length >= 3) {
    const subdomain = parts[0];

    // Ignore common prefixes
    if (['www', 'app', 'api'].includes(subdomain.toLowerCase())) {
      return null;
    }

    return subdomain;
  }

  // No subdomain (e.g., co-taskr.com or single-part hostname)
  return null;
}

/**
 * Get full tenant URL
 */
export function getTenantUrl(slug: string): string {
  const protocol = window.location.protocol;
  const baseDomain = getBaseDomain();
  return `${protocol}//${slug}.${baseDomain}`;
}

/**
 * Get base domain (without subdomain)
 */
export function getBaseDomain(): string {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname.startsWith('127.0.0.1')) {
    return 'localhost:5173';
  }

  const parts = hostname.split('.');

  // For subdomains, return last two parts (e.g., acme.co-taskr.com → co-taskr.com)
  if (parts.length >= 3) {
    return parts.slice(-2).join('.');
  }

  return hostname;
}

/**
 * Check if current environment has tenant context
 */
export function hasTenantContext(): boolean {
  return getTenantSlug() !== null;
}

/**
 * Redirect to tenant subdomain
 */
export function redirectToTenant(slug: string): void {
  const url = getTenantUrl(slug);
  window.location.href = url;
}

/**
 * Store tenant info in session storage
 */
export function setTenantInfo(tenant: Tenant): void {
  sessionStorage.setItem('tenant', JSON.stringify(tenant));
}

/**
 * Get tenant info from session storage
 */
export function getTenantInfo(): Tenant | null {
  const tenantStr = sessionStorage.getItem('tenant');
  if (!tenantStr) return null;

  try {
    return JSON.parse(tenantStr) as Tenant;
  } catch {
    return null;
  }
}

/**
 * Clear tenant info from session storage
 */
export function clearTenantInfo(): void {
  sessionStorage.removeItem('tenant');
}

/**
 * Format tenant display name
 */
export function formatTenantName(tenant: Tenant): string {
  return tenant.name;
}

/**
 * Get usage tier badge color (legacy plan column still supported).
 */
export function getTenantUsageTierColor(tier: 'starter' | 'growth' | 'enterprise'): string {
  switch (tier) {
    case 'starter':
      return 'bg-gray-100 text-gray-800';
    case 'growth':
      return 'bg-blue-100 text-blue-800';
    case 'enterprise':
      return 'bg-indigo-100 text-indigo-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Check if tenant is active
 */
export function isTenantActive(tenant: Tenant): boolean {
  return tenant.status === 'ACTIVE';
}

/**
 * Get tenant status badge color
 */
export function getTenantStatusColor(status: Tenant['status']): string {
  switch (status) {
    case 'ACTIVE':
      return 'bg-green-100 text-green-800';
    case 'TRIAL':
      return 'bg-yellow-100 text-yellow-800';
    case 'SUSPENDED':
      return 'bg-red-100 text-red-800';
    case 'CANCELLED':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}
